import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'buyer' | 'seller' | 'professional';
}

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const { user, profile, activeRole, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        // Not authenticated, redirect to auth page
        navigate('/auth');
        return;
      }

      if (requiredRole && activeRole !== requiredRole) {
        // Wrong role, redirect to correct dashboard
        let correctPath = '/';
        if (activeRole === 'seller') {
          correctPath = '/seller-dashboard';
        } else if (activeRole === 'buyer') {
          correctPath = '/buyer-dashboard';
        } else if (activeRole === 'professional') {
          correctPath = '/professional-dashboard';
        }
        navigate(correctPath);
        return;
      }
    }
  }, [user, profile, loading, navigate, requiredRole]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated or wrong role
  if (!user || (requiredRole && activeRole !== requiredRole)) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;